import { Box, Card, Rating, Stack, styled, Typography, useTheme } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import CarouselComponent from '../../../components/carouselComponent/carouselComponent';
import data from '../../../data/data.json';
import SchoolIcon from '@mui/icons-material/School';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import Title2Component from "../../../components/title/title2Component";

const TestimonialView = () => {
    const theme = useTheme();
    const {t} = useTranslation();

    const getTestimonialsList = useCallback(() => {
        const list = [];

        for(let i = 0; i < data.testimonials.length; i++){
            list.push(<TestimonialDetails client={data.testimonials[i]}/>);
        }

        return list;
    }, [data]);

    return (
        <>
            <Stack
                position='relative'
                direction='column'
                justifyContent='center'
                alignItems='center'
            >
                {/* Upper cloud */}
                <Box
                    sx={{
                        width: '100%',
                        zIndex: 1,
                        backgroundColor: theme.palette.primary.main
                    }}
                >
                    <img 
                        src='/images/vectors/wave bg 2.png' 
                        alt='cloud' 
                        width='100%' 
                        style={{
                            transform: 'translateY(6px)'
                        }}
                    />
                </Box>

                <Box 
                    width='100%'
                    sx={{
                        backgroundColor: theme.palette.background.default,
                        pb: 10
                    }}
                    className="bodySpacing"
                >
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                        px='5%'
                    >
                        <Title2Component title={t('testimonial.title')} description={t('testimonial.description')}/>

                        {
                            data.testimonials.length > 1
                            ?
                            <CarouselComponent list={getTestimonialsList()}/>
                            :
                            <>
                                {
                                    data.testimonials.length === 1
                                    ?
                                    <TestimonialDetails 
                                        client={data.testimonials[0]} 
                                        fullWidth
                                    />
                                    :
                                    <></>
                                }
                            </>
                        }
                    </Stack>
                </Box>

                {/* Lower cloud */}
                <Box
                    sx={{
                        width: '100%',
                        zIndex: 1,
                        backgroundColor: theme.palette.primary.main,
                        transform: 'scaleY(-1)'
                    }}
                >
                    <img 
                        src='/images/vectors/wave bg 2.png' 
                        alt='cloud' 
                        width='100%' 
                        style={{
                            transform: 'translateY(6px)'
                        }}
                    />
                </Box>
            </Stack>
        </>
    );
}

export default TestimonialView;

const StyledRating = styled(Rating)({
  '& .MuiRating-iconFilled': {
    color: '#ff6d75',
  },
  '& .MuiRating-iconHover': {
    color: '#ff3d47',
  },
});

const TestimonialDetails = ({client, fullWidth=false}) => {
    const theme = useTheme();

    return (
        <>
            <Card
                elevation={3}
                sx={{
                    width: fullWidth ? '100%' : '95%',
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 8,
                    minHeight: {xs: 300, md: 350, lg: 300},
                    mt: 0.5,
                    mb: {xs: 3, md: 5},
                    ml: 1,
                    position: 'relative',
                }}
                data-aos="flip-up"
                data-aos-duration="1000"
            >
                <Stack
                    direction='column'
                    justifyContent='space-between'
                    alignItems='start'
                    p={{xs: 3, md: 5}}
                >
                    {/* Reviewer details */}
                    <Stack
                        direction='row'
                        justifyContent='start'
                        alignItems='center'
                        spacing={2}
                        position='absolute'
                    >
                        <Box
                            sx={{
                                width: {xs: 50, md: 80}
                            }}
                        >
                            <img 
                                src={client.profile}
                                alt={client.name} 
                                width='100%'
                                style={{
                                    borderRadius: '50%',
                                    aspectRatio: 1/1
                                }}
                            />
                        </Box>

                        <Stack
                            direction='column'
                            justifyContent='center'
                            alignItems='start'
                            spacing={0.5}
                        >
                            <Typography
                                fontWeight={600}
                                fontSize={{xs: 12, md: 14, lg: 16}}
                                textAlign='left'
                                color={theme.palette.text.primary}
                                lineHeight={1.25}
                            >
                                {client.name}
                            </Typography>

                            <Typography
                                fontWeight={500}
                                fontSize={12}
                                textAlign="left"
                                lineHeight={1.5}
                                color={theme.palette.text.secondary}
                            >
                                {client.role}
                            </Typography>

                            <Typography
                                fontWeight={600}
                                fontSize={10}
                                textAlign="left"
                                lineHeight={1.5}
                                color={theme.palette.text.secondary}
                            >
                                {client.country}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Typography
                        fontWeight={400}
                        fontSize={12}
                        textAlign="left"
                        lineHeight={1.5}
                        pt={{xs: 10, md: 13}}
                        pb={1}
                    >
                        {client.description}
                    </Typography>

                    {
                        client.rating > 0
                        ?
                        <StyledRating
                            name="customized-color"
                            defaultValue={client.rating}
                            getLabelText={(value) => `${value} Heart${value !== 1 ? 's' : ''}`}
                            precision={0.5}
                            icon={<SchoolIcon fontSize="inherit" color="secondary"/>}
                            emptyIcon={<SchoolOutlinedIcon fontSize="inherit" />}
                            readOnly
                            size="small"
                        />
                        :
                        <div></div>
                    }
                </Stack>
            </Card>
        </>
    );
}